<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import FeedArticleMedia from '@/components/common/feed-articles-media.vue';
import FeedDateSelectbox from '@/components/common/feed-date-selectbox.vue';
import {
  createThemeNewsShouldEnable,
  useAutoGenerateSummary,
} from '@/components/feed-summary/composables/use-auto-generate-summary';
import { useThemeSummary } from '@/components/feed-summary/composables/use-theme-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button.vue';
import FeedSummary from '@/components/feed-summary/feed-summary.vue';
import { Article, DateArticles, FeedType, Theme } from '@/types';

const props = defineProps<{
  theme: Theme;
  feed: 'keyword_feed' | 'related_feed';
  feedType: FeedType;
  data: DateArticles;
  dateOptions: { label: string; value: string }[];
  domesticArticleCountBeforeDate: number;
  foreignArticleCountBeforeDate: number;
  removedArticleIds: number[];
  isFirst?: boolean;
}>();

const emit = defineEmits<{
  'select-date': [dateStr: string];
}>();

const getVisibleArticles = (articles: Article[]) =>
  articles.filter(a => !props.removedArticleIds.includes(a.id));

const currentDate = ref(props.data.dateStr);
const selectDate = (value: string | undefined) => {
  if (value === undefined) return;
  emit('select-date', value);

  // セレクトボックスの表示を強制的に戻すために、
  // 選択された値を代入してから元の値を入れ直して変更検知を走らせる
  currentDate.value = value;
  nextTick(() => {
    currentDate.value = props.data.dateStr;
  });
};

const articleCount = computed(() => {
  const domesticCount = getVisibleArticles(
    props.data.domestic.value.articles,
  ).length;
  const foreignCount = getVisibleArticles(
    props.data.foreign.value.articles,
  ).length;

  if (props.feedType === 'domestic') {
    return domesticCount;
  } else if (props.feedType === 'foreign') {
    return foreignCount;
  }
  return domesticCount + foreignCount;
});

const modelType = computed(() => {
  if (props.feed === 'related_feed') {
    return 'related';
  }
  return 'article';
});

const feature = computed(() => {
  return props.feed;
});

const pageName = computed(() => {
  return props.feed;
});

const { summary, generateButtonState, generateSummary, shouldShowSummary } =
  useThemeSummary(
    {
      page_type: 'feed',
      feed_type: props.feedType,
      model_type: modelType.value,
      date: props.data.dateStr,
      theme_id: props.theme.id,
    },
    articleCount,
    pageName.value,
  );

// URLにsummarizeパラメータがあったら自動で要約生成する
const { autoGenerationReason } = useAutoGenerateSummary(
  generateButtonState,
  generateSummary,
  feature,
  createThemeNewsShouldEnable({
    dateStr: props.data.dateStr,
    isFirst: props.isFirst ?? false,
    isChinese: props.theme.is_chinese,
  }),
);

// 自動生成が有効なとき、先頭でなければスクロールする
watch(autoGenerationReason, autoGenReason => {
  if (autoGenReason === undefined) return;
  const index = props.dateOptions.findIndex(
    option => option.value === props.data.dateStr,
  );
  if (index > 0) {
    emit('select-date', props.data.dateStr);
  }
});
</script>
<template>
  <div class="feed-articles-day">
    <div class="date-header">
      <FeedDateSelectbox
        :options="dateOptions"
        :model-value="currentDate"
        @update:model-value="selectDate"
      />
      <FeedSummaryGenerationButton
        v-if="!theme.is_chinese"
        :state="generateButtonState"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <hr class="header-divider" />
    <FeedSummary
      v-if="!theme.is_chinese && shouldShowSummary(summary)"
      :feed-summary="summary"
      :feature="feature"
      :page-name="pageName"
      :theme-id="theme.id"
      class="feed-summary"
    />
    <FeedArticleMedia
      v-if="
        (feedType === 'all' || feedType === 'domestic') &&
        (!theme || !theme.is_chinese)
      "
      :article-count-before-date="domesticArticleCountBeforeDate"
      :articles="getVisibleArticles(data.domestic.value.articles)"
      :article-count="data.domestic.value.feedCount"
      :theme="theme"
      :date="data.date"
      :date-str="data.dateStr"
      :date-label="data.dateLabel"
      :feed-data="data.domestic.value"
      :is-visible-empty-feed="
        data.domestic.value.isLoaded &&
        getVisibleArticles(data.domestic.value.articles).length === 0
      "
      :feed="feed"
      :display-feed-type="feedType"
      :theme-feed-type="'domestic'"
      @select-date="$emit('select-date', $event)"
    />
    <FeedArticleMedia
      v-if="feedType === 'all' || feedType === 'foreign'"
      :article-count-before-date="foreignArticleCountBeforeDate"
      :articles="getVisibleArticles(data.foreign.value.articles)"
      :article-count="data.foreign.value.feedCount"
      :theme="theme"
      :date="data.date"
      :date-str="data.dateStr"
      :date-label="data.dateLabel"
      :feed-data="data.foreign.value"
      :is-visible-empty-feed="
        data.foreign.value.isLoaded &&
        getVisibleArticles(data.foreign.value.articles).length === 0
      "
      :feed="feed"
      :display-feed-type="feedType"
      :theme-feed-type="'foreign'"
      @select-date="$emit('select-date', $event)"
    />
  </div>
</template>
<style lang="scss" scoped>
.date-header {
  position: sticky;
  top: var(--layout-sticky-top-under-tabs, 112px);
  z-index: 1;

  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 16px;
  background: $color-white;
  border-radius: 4px;

  .generation-button {
    margin-left: auto;
  }
}

.feed-summary {
  margin: 0 12px 16px;
}

.header-divider {
  border: none;
  border-top: 1px solid $color-gray200;
  padding-bottom: 16px;
  background: $color-white;
}

.feed-articles-day {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  border-radius: 4px;
  background: $color-white;
}
</style>
